/**
 * audit-room.tsx — the audit trail's reader, as a room behind Settings.
 *
 * ## Why it exists
 *
 * The trail records sessions, tool calls, approvals (automatic ones too),
 * policy refusals, machine pairing and both settings doors, durably and
 * hash-chained across restarts. Until this room nothing read it: no screen, and
 * no consumer in the client, the React package or the embed. A trail nobody can
 * open is, to the person who needs it, indistinguishable from no trail.
 *
 * ## Who sees what, and why none of it is decided here
 *
 * The server decides, in `GET /api/audit`: somebody holding
 * `chat42.admin.manage` reads the organisation's trail, everybody else reads
 * only the entries where they are the actor, and a paired machine's credential
 * reads none of it. `/api/me` says which of those a person has, so the room can
 * say it before the first page arrives, and each page says which rule it was
 * read under. The room shows both and enforces neither: the narrowing is in the
 * server's query, and a room that hid or filtered entries itself would be a
 * client deciding who may see what. So the room is drawn wherever Settings is,
 * says which view it is showing and who can see more, and shows a refusal with
 * the permission and the role that grants it, never as a fault.
 *
 * ## What it will not claim
 *
 * No "intact" badge. The server checks each record it returns against the hash
 * it was written with, using a key the browser does not hold, and says outright
 * that it did not check the links between records (`chainChecked: false`): one
 * organisation's records are a slice of a chain that runs across every
 * organisation. A green mark built from that would be the screen vouching for
 * something it cannot see. A *failed* check is shown, plainly and in ochre,
 * because a failure is a fact whatever the reach of the check that found it.
 *
 * ## Paging and filtering, on the server
 *
 * A page is fifty entries, newest first, and the next one is asked for with the
 * cursor the last one returned — so every entry the trail holds is reachable,
 * however old. The two filters go to the server too, and cover the whole trail
 * rather than what happens to be loaded. Pages already read are kept while the
 * room is open, so going back to a newer one is instant and does not ask again.
 *
 * Everything a person must read is text on the screen. The only `title` in the
 * room repeats, as a machine timestamp, a time that is already written out.
 */

import { useCallback, useEffect, useId, useRef, useState, type RefObject } from 'react';
import { HttpError, type Chat42Client } from '@chat42/client';
import type { AuditAction, AuditRecord } from '@chat42/runtime';
import { useChat42, useChat42State } from './context.ts';
import { useAuditStyles } from './audit-styles.ts';
import { money } from './setting-parts.tsx';

type AuditPage = Awaited<ReturnType<Chat42Client['audit']>>;

/** Entries per page. Enough to fill a 4K list, few enough to paint on a phone. */
export const PAGE = 50;

/** One page as it was read, and when. */
interface Read { page: AuditPage; at: number }

interface Filters { kind: string; actor: string }
const ALL: Filters = { kind: '', actor: '' };

type Load =
  | { at: 'loading' }
  /** Pages read so far for `filters`, newest first. */
  | { at: 'ready'; reads: Read[]; filters: Filters }
  | { at: 'refused'; permission: string; role: string; held: string[] }
  | { at: 'failed'; message: string };

const NONE: readonly AuditRecord[] = [];

// ---- what each kind of entry is called ---------------------------------------

interface Words {
  /** What happened, as a heading, and the kind's name in the filter. */
  what: string;
  /** A heading for one entry, where its detail makes it more particular than `what`. */
  heading?: (r: AuditRecord) => string | undefined;
  /** What kind of thing the subject is, where the action settles it. */
  against?: string | ((r: AuditRecord) => string);
  /**
   * The subject is the new state — a mode, a preset, a tier — rather than the
   * thing acted on, which is then the conversation.
   */
  subjectIsNow?: boolean;
  /** Recorded against a stand-in conversation id that names no conversation. */
  noConversation?: boolean;
  /** One sentence from the detail, where raw keys would not read. */
  says?: (r: AuditRecord) => string | undefined;
  /** Detail keys the sentence already says, so they are not listed twice. */
  uses?: readonly string[];
  /**
   * `detail.by` names the person who did it — an address, as recorded with the
   * entry — rather than something else that did it, like auth.
   */
  byIsWho?: boolean;
}

const text = (r: AuditRecord, key: string): string | undefined => {
  const v = r.detail?.[key];
  return typeof v === 'string' && v ? v : undefined;
};
const count = (r: AuditRecord, key: string): number | undefined => {
  const v = r.detail?.[key];
  return typeof v === 'number' ? v : undefined;
};
const flag = (r: AuditRecord, key: string): boolean | undefined => {
  const v = r.detail?.[key];
  return typeof v === 'boolean' ? v : undefined;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * A settings entry, in a sentence.
 *
 * The trail holds key names, a count and how many were raised — never a value
 * — and the sentence says exactly that much, so nobody reads it as "and here is
 * what it was".
 */
function keysSaid(r: AuditRecord): string {
  const keys = text(r, 'keys');
  const n = count(r, 'count') ?? (keys ? keys.split(',').length : 0);
  const raised = count(r, 'raised') ?? 0;
  return `${plural(n, 'setting')} changed${keys ? `: ${keys}` : ''}. `
    + `${raised ? `${raised.toLocaleString()} raised` : 'None raised'}.`;
}

const reason = (r: AuditRecord) => text(r, 'reason');

/**
 * A monthly spending limit's change, in a sentence: from what, to what (2026-10-28). Dollars a
 * month, 0 meaning none. The limit before is missing from an entry written before it was kept, or
 * where it could not be read, and the sentence then says only where it went.
 */
function limitSaid(r: AuditRecord): string | undefined {
  const to = count(r, 'monthlyCapUsd');
  if (to === undefined) return undefined;
  const from = count(r, 'previousCapUsd');
  const said = (usd: number) => (usd === 0 ? 'no limit' : `${money(usd)} a month`);
  const now = said(to);
  return from === undefined ? `Set to ${now}` : `From ${said(from)} to ${now}`;
}

/** Which way a workspace's read-only setting went, as the git door now answers its pushes. */
function readOnlySaid(r: AuditRecord): string | undefined {
  const readOnly = flag(r, 'readOnly');
  if (readOnly === undefined) return undefined;
  return readOnly ? 'Made read-only: its pushes are refused at the git door' : 'Let push again';
}

/**
 * A git operation, in a sentence.
 *
 * The entry holds a result, a host and two byte counts, and this says them and
 * nothing more. `relayed` is the door's word and is kept as the door meant it:
 * the git host answered and its answer was passed on. Whether the host took
 * every branch of a push is not something the entry knows, so it is not said.
 */
const GIT_USES = ['result', 'host', 'bytesUp', 'bytesDown', 'for'] as const;
function gitSaid(r: AuditRecord): string | undefined {
  const result = text(r, 'result');
  if (!result) return undefined;
  const host = text(r, 'host');
  const at = host ? ` ${r.action === 'git.push' ? 'to' : 'from'} ${host}` : '';
  const forWhat = text(r, 'for') ? `, for ${text(r, 'for')}` : '';
  if (result.startsWith('refused:')) return `Refused${at}: ${result.slice('refused:'.length)}${forWhat}`;
  const up = count(r, 'bytesUp') ?? 0;
  const down = count(r, 'bytesDown') ?? 0;
  const moved = `${plural(up, 'byte')} sent, ${plural(down, 'byte')} received`;
  return result === 'cut'
    ? `Cut off part way${at}: ${moved}${forWhat}`
    : `Relayed${at}: ${moved}${forWhat}`;
}

/**
 * The sign-in's refusals (ADR 0140), by `detail.reason`. Each says what was used where, in the
 * words an administrator reading the trail would use; the door itself is the subject.
 */
const REFUSED_AT_THE_DOOR: Record<string, { what: (r: AuditRecord) => string; says: (r: AuditRecord) => string }> = {
  not_for_machines: {
    what: () => 'Refused: a machine\'s credential',
    says: () => 'A paired machine\'s credential was used for something other than its own work.',
  },
  machine_only: {
    what: () => 'Refused: a machine\'s connection',
    says: () => 'A person\'s sign-in was used on the connection only a paired machine may open.',
  },
  missing_permission: {
    what: (r) => `Refused: ${DOOR_WORDS[r.subject ?? ''] ?? 'without a permission it needs'}`,
    says: (r) => {
      const permission = text(r, 'permission');
      const needs = permission ? PERMISSION_WORDS[permission] ?? permission : 'a permission this account does not have';
      const role = text(r, 'role');
      return `Needs ${needs}${role ? `, which the ${role} role grants` : ''}.`;
    },
  },
};

/** What a door refused, where the subject names one a person would recognise. */
const DOOR_WORDS: Record<string, string> = {
  'POST /api/devices/pair': 'pairing a machine',
  'POST /api/devices/installer': 'pairing a machine',
};

/**
 * Chat42's permissions as a reader of the trail would say them. The id is shown where one is
 * missing here, so a permission added later still reads, if less kindly.
 */
const PERMISSION_WORDS: Record<string, string> = {
  'chat42.access': 'access to Chat42',
  'chat42.sessions.use': 'the right to hold conversations',
  'chat42.credits.read': 'the right to see the balance',
  'chat42.credits.buy': 'the right to add credit',
  'chat42.workspace.use': 'the code features',
  'chat42.admin.manage': 'the right to run the organisation\'s settings',
  'chat42.platform.manage': 'the right to configure the deployment',
};

const WORDS: Record<AuditAction, Words> = {
  'session.created': { what: 'Conversation started', against: 'Conversation' },
  'session.closed': {
    what: 'Conversation closed',
    against: 'Conversation',
    says: (r) => (flag(r, 'deleted') ? 'Deleted' : text(r, 'outcome') ? `Outcome: ${text(r, 'outcome')}` : undefined),
    uses: ['deleted', 'outcome'],
  },
  'session.caps': {
    // What was actually allowed, not "caps changed": a trail is read by
    // somebody asking why a run cost what it cost, and the number is the answer.
    what: 'A limit on this conversation changed',
    against: 'Conversation',
    says: (r) => {
      const steps = count(r, 'iterations');
      if (steps === undefined) return text(r, 'why');
      return steps >= 100_000
        ? 'It no longer stops at the step ceiling; spend still bounds it'
        : `Step ceiling now ${steps}`;
    },
    uses: ['iterations', 'why'],
  },
  'session.mode': { what: 'How far it may go changed', subjectIsNow: true },
  'session.preset': { what: 'Reasoning preset changed', subjectIsNow: true },
  'session.tier': { what: 'Where it runs changed', subjectIsNow: true },
  // A record's Read-only setting reaching code the conversation already had (ADR 0076). Who
  // asked is shown: before ADR 0089 anybody in the organisation could make it read-only, so an
  // older entry may name somebody other than the conversation's own person.
  'session.read-only': {
    what: 'Read-only changed', against: 'Repository', says: readOnlySaid,
    uses: ['readOnly', 'credentials', 'by'], byIsWho: true,
  },
  // Repositories opened beside the code a conversation already had, as a record linking
  // several reaches it (ADR 0087). Which ones is the subject.
  'session.workspaces': {
    what: 'Code added to the conversation', against: 'Repositories',
    says: (r) => (flag(r, 'readOnly') ? 'Opened read-only: its pushes are refused at the git door' : undefined),
    uses: ['readOnly'],
  },
  // An administrator opened somebody else's conversation to read it (ADR 0154). The actor is the
  // administrator; the conversation is the owner's. Anything beyond reading is `access.refused`.
  'session.admin_read': {
    what: "Administrator read a colleague's conversation", against: 'Asked through',
    says: () => "Opened read only by an organisation administrator. It was not the administrator's own",
  },
  // Somebody asked for a colleague's conversation or schedule and was answered as if it were not
  // there (ADR 0089). The subject is the route they asked through; the actor is who asked.
  // Also the sign-in's refusals (ADR 0140), told apart by `detail.reason`.
  'access.refused': {
    what: 'Refused access',
    heading: (r) => REFUSED_AT_THE_DOOR[text(r, 'reason') ?? '']?.what(r) ?? 'Refused: somebody else\'s',
    against: 'Asked through',
    says: (r) => {
      const door = REFUSED_AT_THE_DOOR[text(r, 'reason') ?? ''];
      const repeats = count(r, 'repeats');
      // Counted since the last entry like this, which may be long before this one: the count rides
      // on the next refusal at the same door, whenever that comes, so it names no window.
      const more = repeats ? ` Refused ${plural(repeats, 'more time')} since the last entry like this.` : '';
      if (door) return `${door.says(r)}${more}`;
      return text(r, 'scheduleId')
        ? `A schedule that belongs to somebody else (${text(r, 'scheduleId')})`
        : 'A conversation that belongs to somebody else';
    },
    uses: ['scheduleId', 'reason', 'permission', 'role', 'repeats'],
  },
  // A workspace's git, through Chat42's git door. Headed by what was
  // attempted; the sentence under it is the entry's own account of what came
  // of it, in the words it was recorded with, so a refusal is never headed as
  // though it happened.
  'git.fetch': { what: 'Git fetch from a workspace', against: 'Repository', says: gitSaid, uses: GIT_USES },
  'git.push': { what: 'Git push from a workspace', against: 'Repository', says: gitSaid, uses: GIT_USES },
  'tool.called': { what: 'Tool called', against: 'Tool' },
  'file.changed': {
    what: 'File changed',
    against: 'File',
    says: (r) => {
      const n = count(r, 'discarded');
      return n === undefined ? undefined : `${plural(n, 'change')} put back`;
    },
    uses: ['discarded'],
  },
  'changes.settled': {
    what: 'Changes settled',
    says: (r) => {
      const how = text(r, 'how');
      const files = count(r, 'files') ?? 0;
      const failed = count(r, 'failed');
      const verb = how === 'accepted' ? 'Accepted' : how === 'rejected' ? 'Put back' : how ?? 'Settled';
      return `${verb} ${plural(files, 'file')}${failed ? `, ${failed.toLocaleString()} failed` : ''}`;
    },
    uses: ['how', 'files', 'failed'],
  },
  'change.restored': {
    what: 'Change put back',
    against: 'What was changed',
    says: (r) => {
      const tool = text(r, 'tool');
      const chars = count(r, 'characters');
      const size = chars === undefined ? '' : ` — ${plural(chars, 'character')}`;
      return `Restored from the copy kept before ${tool ?? 'the call'} ran${size}`;
    },
    uses: ['tool', 'source', 'edit', 'characters'],
  },
  /**
   * The largest grant a person makes to a connector: after it, that service's
   * own HTML runs in a frame in the product. Said as the thing it is rather
   * than as a setting, because a reader asking "how could that service do
   * that?" is asking about this row.
   */
  'apps.trusted': {
    what: 'A service was allowed to draw in Chat42',
    against: 'Service',
    says: (r) => text(r, 'label'),
    uses: ['label', 'connectionId'],
    noConversation: true,
  },
  'apps.refused': {
    what: 'A service was no longer allowed to draw in Chat42',
    against: 'Service',
    says: (r) => text(r, 'label'),
    uses: ['label', 'connectionId'],
    noConversation: true,
  },
  'library.published': { what: 'Organisation library changed', against: 'Library entry', noConversation: true },
  'library.reverted': { what: 'Organisation library put back', against: 'Library entry', noConversation: true },
  'approval.requested': { what: 'Approval asked for', against: 'Action', says: reason, uses: ['reason'] },
  'approval.resolved': {
    what: 'Approval decided',
    against: 'Action',
    says: (r) => {
      const approved = flag(r, 'approved');
      const verdict = approved === undefined ? 'Decided' : approved ? 'Approved' : 'Declined';
      const by = flag(r, 'auto') ? 'automatically, by policy' : r.approvedBy ? `by ${r.approvedBy}` : '';
      const why = reason(r);
      return `${verdict}${by ? ` ${by}` : ''}${why ? `: ${why}` : ''}`;
    },
    uses: ['approved', 'auto', 'reason'],
  },
  'question.asked': {
    what: 'Question asked',
    against: 'Question',
    // The trail keeps what kind of question and a fingerprint, never its words;
    // the words are in the conversation. Entries from before that change
    // carry the header they were written with, shown as recorded.
    says: (r) => {
      const kind = text(r, 'kind');
      const options = count(r, 'options');
      const who = kind === 'coordinator' ? 'Asked the coordinator' : kind === 'person' ? 'Asked the person' : undefined;
      if (!who) return undefined;
      return options === undefined ? who : `${who}, ${plural(options, 'option')}`;
    },
    uses: ['kind', 'options'],
  },
  'question.answered': {
    what: 'Question answered',
    against: 'Question',
    says: (r) => (flag(r, 'skipped') ? 'Skipped' : text(r, 'chosen') ? `Chose ${text(r, 'chosen')}` : undefined),
    uses: ['chosen', 'skipped'],
  },
  'device.enrolled': { what: 'Machine paired', against: 'Machine' },
  'device.revoked': { what: 'Machine unpaired', against: 'Machine' },
  'device.deleted': { what: 'Unpaired machine removed from the list', against: 'Machine' },
  'device.run-permission.granted': { what: 'Machine allowed to run schedules', against: 'Machine' },
  'device.run-permission.withdrawn': { what: 'Machine’s schedule permission withdrawn', against: 'Machine' },
  'plugin.applied': {
    what: 'A plugin changed something',
    against: (r) => (text(r, 'hook')?.startsWith('tool:') ? 'Tool' : 'Hook'),
    says: (r) => (text(r, 'hook') ? `At ${text(r, 'hook')}` : undefined),
    uses: ['hook'],
  },
  // Information, not an alarm: the policy did what it is for.
  'policy.denied': { what: 'Refused by policy', against: 'Tool', says: reason, uses: ['reason'] },
  'tools.withheld': { what: 'Tools withheld from an agent', against: 'Work item' },
  'task.spawned': { what: 'Background task started', against: 'Task' },
  'task.stopped': { what: 'Background task stopped', against: 'Task' },
  'platform.autotopup.changed': {
    what: 'Automatic top-up changed by a platform administrator', against: 'Account', byIsWho: true, uses: ['by'],
  },
  'platform.spendlimit.changed': {
    what: 'Monthly spend limit changed by a platform administrator',
    against: 'Account',
    says: limitSaid,
    byIsWho: true,
    uses: ['by', 'monthlyCapUsd', 'previousCapUsd'],
  },
  'spendlimit.changed': {
    what: 'Monthly spending limit changed',
    against: 'Organisation',
    says: limitSaid,
    byIsWho: true,
    uses: ['by', 'monthlyCapUsd', 'previousCapUsd'],
  },
  // A turn refused at a monthly AI limit (money-right-11): which one, whose amount, never the figures.
  'spending.refused': {
    what: 'Turn refused at a monthly AI limit',
    heading: (r) => (text(r, 'scope') === 'person' ? 'Turn refused: AI budget used up' : text(r, 'scope') === 'org' ? 'Turn refused: monthly AI limit reached' : undefined),
    against: 'Conversation',
    says: (r) => {
      const scope = text(r, 'scope');
      const whose = scope === 'person'
        ? (text(r, 'source') === 'default' ? "The person's budget, from the organisation's default" : "The person's own budget")
        : scope === 'org' ? "The organisation's monthly limit" : undefined;
      if (!whose) return undefined;
      return r.detail?.['run'] === true ? `${whose}. The run is held until next month, or sooner if an administrator raises it.` : `${whose}.`;
    },
    uses: ['scope', 'source', 'run'],
  },
  'settings.changed': {
    what: 'Settings changed',
    // A plugin's settings door records the agent it changed; the product's own
    // records the person whose settings they are.
    against: (r) => (r.pluginId ? 'Agent' : 'Person'),
    says: keysSaid,
    uses: ['keys', 'count', 'raised', 'by'],
    byIsWho: true,
  },
  'settings.org.changed': {
    what: 'Organisation policy changed',
    against: 'Organisation',
    says: keysSaid,
    uses: ['keys', 'count', 'raised', 'by'],
    byIsWho: true,
  },
};

/** Every kind the trail records, by its heading: the choices for the filter, whether or not any are loaded. */
const KINDS = (Object.keys(WORDS) as AuditAction[])
  .map((id) => ({ id: id as string, label: WORDS[id].what }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * The words for an entry. An action this build does not know — a server newer
 * than its screen — is shown by its own name rather than dropped, because a row
 * missing from an audit list is the one failure it cannot have.
 */
function wordsFor(action: string): Words {
  return (WORDS as Record<string, Words | undefined>)[action] ?? { what: action };
}

/** The heading for one entry: its own, where its kind words one, else the kind's. */
function headingOf(r: AuditRecord): string {
  const words = wordsFor(r.action);
  return words.heading?.(r) ?? words.what;
}

/** Who did it, as the entry itself records them: the address it carries, if it carries one. */
function recordedAs(r: AuditRecord): string | undefined {
  if (!wordsFor(r.action).byIsWho) return undefined;
  const by = text(r, 'by');
  return by && by !== r.actor ? by : undefined;
}

// ---- time --------------------------------------------------------------------

const TIME = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const DAY = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

function dayLabel(d: Date, now: Date): string {
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const said = DAY.format(d);
  if (dayKey(d) === dayKey(now)) return `Today, ${said}`;
  if (dayKey(d) === dayKey(yesterday)) return `Yesterday, ${said}`;
  return said;
}

/** The viewer's zone, said once at the top, so a time in the list is never ambiguous. */
function zone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

// ---- small words -------------------------------------------------------------

/** A server's message as the end of a sentence, whether or not it brought its own full stop. */
function sentence(message: string): string {
  const said = message.trim().replace(/[.\s]+$/, '');
  return said ? `${said}.` : 'The server gave no reason.';
}

/** How the value of a detail reads: yes and no for a flag, as recorded otherwise. */
function shown(v: string | number | boolean): string {
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return typeof v === 'number' ? v.toLocaleString() : v;
}

// ---- the room ----------------------------------------------------------------

export function AuditRoom() {
  useAuditStyles();
  const { client } = useChat42();
  const { me } = useChat42State();
  const [load, setLoad] = useState<Load>({ at: 'loading' });
  /** Which page is on screen. */
  const [index, setIndex] = useState(0);
  /** A read in flight. What is on screen stays up underneath it rather than blanking. */
  const [busy, setBusy] = useState<'refresh' | 'filter' | 'older' | undefined>();
  /** A read that failed while something was already on screen. */
  const [problem, setProblem] = useState<string | undefined>();
  /**
   * The filters as chosen, which lead the ones the pages on screen were read
   * with: a choice is drawn the moment it is made, and the list catches up
   * when the server answers.
   */
  const [chosen, setChosen] = useState<Filters>(ALL);
  /** An entry somebody asked to be shown: scrolled to once it is drawn. */
  const [seek, setSeek] = useState<string | undefined>();
  const ids = useId();
  const listTop = useRef<HTMLDivElement>(null);
  /**
   * Everybody seen acting in any page read while the room is open, with the
   * address their entries record, for the "Who" filter. The server has no
   * list of people to offer and should not grow one for this; somebody not
   * yet seen can still be found by reading on.
   */
  const people = useRef(new Map<string, string | undefined>());

  /**
   * The newest request wins. A refresh, a filter and a page can overlap, and
   * an answer that arrives after a newer one was asked for is simply dropped.
   * Also bumped on unmount, so an answer for a room somebody has left sets
   * nothing.
   */
  const ticket = useRef(0);
  useEffect(() => () => { ticket.current += 1; }, []);

  const read = useCallback(async (filters: Filters, older?: { reads: Read[]; before: string }) => {
    const mine = ++ticket.current;
    try {
      const page = await client.audit({
        limit: PAGE,
        ...(older ? { before: older.before } : {}),
        ...(filters.kind ? { action: filters.kind } : {}),
        ...(filters.actor ? { actor: filters.actor } : {}),
      });
      if (mine !== ticket.current) return;
      for (const r of page.records) {
        if (!people.current.has(r.actor) || !people.current.get(r.actor)) people.current.set(r.actor, recordedAs(r));
      }
      const reads = [...(older?.reads ?? []), { page, at: Date.now() }];
      setLoad({ at: 'ready', reads, filters });
      setIndex(reads.length - 1);
      setProblem(undefined);
      // To the top of the list for a page turn, like a turn to a page already
      // read. Not for a filter or a refresh: the controls just used sit above
      // the list, and scrolling would take them off the screen.
      if (older) listTop.current?.scrollIntoView?.({ block: 'start' });
    } catch (err) {
      if (mine !== ticket.current) return;
      const refused = err instanceof HttpError ? err.permission : undefined;
      if (refused) {
        setLoad({ at: 'refused', ...refused });
      } else {
        const message = (err as Error).message || 'The server gave no reason.';
        // Keep what is on screen: an old read with a note beside it is worth
        // more than an error where a list was.
        setLoad((was) => (was.at === 'ready' ? was : { at: 'failed', message }));
        setProblem(message);
      }
    } finally {
      if (mine === ticket.current) setBusy(undefined);
    }
  }, [client]);

  /** A paired machine's credential reads none of the trail, and the server has said so already. */
  const machine = me?.auditView === 'none';
  useEffect(() => {
    if (!machine) void read(ALL);
  }, [read, machine]);

  const ready = load.at === 'ready' ? load : undefined;
  const reads = ready?.reads ?? [];
  const shownAt = Math.min(index, Math.max(0, reads.length - 1));
  const current = reads[shownAt];
  const page = current?.page;
  const records = page?.records ?? NONE;
  const filters = ready?.filters ?? ALL;
  const filtered = Boolean(filters.kind || filters.actor);
  /** Where this page starts, counting what the newer pages showed. */
  const from = reads.slice(0, shownAt).reduce((n, r) => n + r.page.records.length, 0);
  /** Whether the server can page at all: one older than this build sends no view and no cursor. */
  const pages = page?.view !== undefined;
  const hasOlder = shownAt + 1 < reads.length || Boolean(page?.before);

  /** Which part of the trail this is. The page's own word first, then what the server said about the person. */
  const view = page?.view ?? me?.auditView;

  /** What the server's check found, if it found anything wrong, among the entries on screen. */
  const verdict = page?.verified;
  // `brokenAt` counts from the oldest, because the server checks oldest first
  // and the records arrive newest first.
  const broken = verdict && verdict.ok === false
    ? records[records.length - 1 - verdict.brokenAt]
    : undefined;

  useEffect(() => {
    if (!seek) return;
    document.getElementById(`${ids}-${seek}`)?.scrollIntoView?.({ block: 'center' });
    setSeek(undefined);
  }, [seek, ids]);

  const turn = (to: number) => {
    setIndex(to);
    listTop.current?.scrollIntoView?.({ block: 'start' });
  };
  const older = () => {
    if (!ready) return;
    if (shownAt + 1 < reads.length) {
      turn(shownAt + 1);
      return;
    }
    if (!page?.before) return;
    setBusy('older');
    void read(ready.filters, { reads, before: page.before });
  };
  const refresh = () => {
    setBusy('refresh');
    void read(ready?.filters ?? chosen);
  };
  const retry = () => {
    setLoad({ at: 'loading' });
    void read(chosen);
  };
  const pick = (next: Partial<Filters>) => {
    const to = { ...chosen, ...next };
    setChosen(to);
    setBusy('filter');
    void read(to);
  };

  const where = me?.orgName ? ` in ${me.orgName}` : me?.personal ? ' on your account' : ' in this organisation';
  const tz = zone();

  return (
    <div className="c42-page"><div className="c42-audit">
      <div className="c42-audit-top">
        <div className="c42-phd">
          <h2>Audit trail</h2>
          <p>
            Who did what, when, and to what{where}. Newest first, with times in your
            time zone{tz ? ` (${tz})` : ''}.
          </p>
          <ViewSaid view={view} wider={page?.wider} where={where} personal={me?.personal === true} />
        </div>
        <Limits chainChecked={page?.chainChecked ?? false} />
      </div>

      {machine && (
        <div className="c42-unavail denied" role="note">
          <b>Not for a paired machine</b>
          <p>
            A paired machine’s credential does not read the audit trail: it is a
            record of what people did. Sign in as a person to read it.
          </p>
        </div>
      )}

      {!machine && load.at === 'loading' && (
        <div className="c42-empty" aria-busy="true">Loading the audit trail…</div>
      )}

      {load.at === 'refused' && (
        <div className="c42-unavail denied" role="note">
          <b>Not yours to read</b>
          <p>
            Reading this needs <code className="c42-code">{load.permission}</code>,
            which the <code className="c42-code">{load.role}</code> role grants at the
            identity provider. {load.held.length
              ? `This account holds ${load.held.join(', ')}.`
              : 'This account holds no Chat42 permissions at all.'}
          </p>
          <p>That is a refusal, not an empty trail. Ask whoever administers your organisation’s access.</p>
        </div>
      )}

      {load.at === 'failed' && (
        <div className="c42-unavail gone" role="note">
          <b>The audit trail did not load</b>
          <p>{load.message}</p>
          <p>Nothing is known either way until it does: this is the request failing, not the trail.</p>
          <button className="c42-mini" onClick={retry}>Try again</button>
        </div>
      )}

      {ready && current && page && (
        <>
          {problem && (
            <div className="c42-banner" role="alert">
              Could not read the trail again: {sentence(problem)} What is shown was
              read at {TIME.format(current.at)}.
            </div>
          )}

          {verdict && verdict.ok === false && (
            <div className="c42-audit-broken" role="alert">
              <b>An entry does not match the hash it was written with</b>
              <p>
                The server checks the entries it sends against their hashes, oldest
                first, and stopped on this page at {broken
                  ? <>entry #{broken.seq}: {headingOf(broken)}, {DAY.format(new Date(broken.at))} at {TIME.format(new Date(broken.at))}</>
                  : 'an entry it sent'}. {verdict.reason} Newer entries on this page than that one were not checked.
              </p>
              {broken && (
                <button className="c42-mini" onClick={() => setSeek(broken.hash.slice(0, 16))}>Show this entry</button>
              )}
            </div>
          )}

          <Toolbar
            ids={ids} me={me?.userId} view={view} chosen={chosen} people={people.current}
            filtered={Boolean(chosen.kind || chosen.actor)} onPick={pick}
            loadedAt={current.at} busy={busy} onRefresh={refresh} count={records.length}
          />

          {chosen.kind.startsWith('settings.') && (
            <p className="c42-audit-note">
              A settings entry names the keys that changed, how many, and how many
              were raised. It never records what a setting was, or what it became.
            </p>
          )}

          <PageFacts page={page} />

          {records.length === 0 && from === 0 && !page.before ? (
            filtered ? (
              <div className="c42-empty">
                Nothing on the trail matches these filters.{' '}
                <button className="c42-mini" onClick={() => pick(ALL)}>Clear filters</button>
              </div>
            ) : (
              <div className="c42-empty">
                {view === 'own' ? 'Nothing you have done' : 'Nothing'} has been recorded{where} yet.
                Conversations, tool calls, approvals, paired machines and settings changes
                appear here as they happen.
              </div>
            )
          ) : (
            <Entries
              top={listTop} ids={ids} rows={records} me={me?.userId}
              broken={broken?.hash} busy={busy === 'refresh' || busy === 'filter'}
            />
          )}

          {(shownAt > 0 || hasOlder) && (
            <nav className="c42-audit-pager" aria-label="Pages of the audit trail">
              {shownAt >= 2 && <button className="c42-mini" onClick={() => turn(0)}>Newest</button>}
              <button className="c42-mini" disabled={shownAt === 0} onClick={() => turn(shownAt - 1)}>
                Newer
              </button>
              <span role="status">
                {records.length
                  ? `Entries ${(from + 1).toLocaleString()}–${(from + records.length).toLocaleString()}`
                  : 'No entries on this page'}
              </span>
              <button className="c42-mini" disabled={!hasOlder || busy !== undefined} onClick={older}>
                {busy === 'older' ? 'Reading older entries…' : 'Older'}
              </button>
            </nav>
          )}

          {(records.length > 0 || from > 0) && !hasOlder && (
            <End pages={pages} filtered={filtered} full={records.length >= PAGE} />
          )}
        </>
      )}
    </div></div>
  );
}

// ---- the parts ---------------------------------------------------------------

/**
 * Which part of the trail is on screen, and who can see more.
 *
 * Said from the server's own answer — the page's `view`, or `/api/me` before
 * the first page arrives — and never worked out here. A server older than the
 * narrowed view says neither, and then nothing is claimed.
 */
function ViewSaid({ view, wider, where, personal }: {
  view: 'organisation' | 'own' | 'none' | undefined;
  wider: { permission: string; role: string } | undefined;
  where: string;
  personal: boolean;
}) {
  if (view === 'organisation') {
    return (
      <p className="c42-audit-view" role="note">
        <b>{personal ? 'Everything recorded on your account.' : 'The organisation’s trail: everyone’s entries.'}</b>{' '}
        Somebody without the administrator’s permission sees only their own entries here.
      </p>
    );
  }
  if (view === 'own') {
    return (
      <p className="c42-audit-view" role="note">
        <b>Your own entries: what you did{where}.</b>{' '}
        {wider
          ? <>Everyone’s entries are for somebody holding <code className="c42-code">{wider.permission}</code>, which the <code className="c42-code">{wider.role}</code> role grants.</>
          : 'Everyone’s entries are for an administrator of the organisation.'}
      </p>
    );
  }
  return null;
}

/**
 * What the trail cannot answer, said before anybody goes looking.
 *
 * On the screen rather than in help, because it is a decision people do not
 * expect: a settings entry records key names, a count and how many were raised,
 * and never a value. "Who changed this last Tuesday" has an answer here and
 * "what was it before" does not, and somebody who expected the second should
 * learn that from the room rather than from an empty search.
 */
function Limits({ chainChecked }: { chainChecked: boolean }) {
  return (
    <div className="c42-audit-limits" role="note">
      <b>What this trail cannot tell you</b>
      <ul>
        <li>
          <em>What a setting was, or what it became.</em> A settings entry records
          which keys changed, how many, and how many were raised — never a value.
          So “who changed this last Tuesday” has an answer here, and “what was it
          before” does not.
        </li>
        <li>
          <em>What was said or written.</em> An entry names a file, not its
          contents; a tool, not its output; that a question was asked, not its words.
        </li>
        {chainChecked ? null : (
          <li>
            <em>Whether anything is missing.</em> The chain linking entries runs
            through every organisation’s at once and is not checked on this
            screen, so a removed entry would not show.
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * Facts about the page on screen that are not about any one entry.
 *
 * Each is said as a fact, in grey: none of them is an alarm, and each is
 * worded to claim no more than the server can know from one page.
 */
function PageFacts({ page }: { page: AuditPage }) {
  const verdict = page.verified;
  const unsigned = verdict.ok ? verdict.unsigned ?? 0 : 0;
  const uncovered = verdict.ok ? verdict.detailsNotCovered ?? 0 : 0;
  const unreadable = page.unreadable ?? 0;
  if (!unsigned && !uncovered && !unreadable) return null;
  return (
    <>
      {unreadable > 0 && (
        <p className="c42-audit-note">
          {plural(unreadable, 'stored entry', 'stored entries')} on this page could not be read,
          so {unreadable === 1 ? 'it is' : 'they are'} counted here rather than shown.
        </p>
      )}
      {uncovered > 0 && (
        <p className="c42-audit-note">
          {plural(uncovered, 'entry', 'entries')} here {uncovered === 1 ? 'was' : 'were'} written before an
          entry’s details were covered by its hash. Their other fields are checked; their details are not.
        </p>
      )}
      {unsigned > 0 && (
        // Not "written before signing began": a page cannot see the signed
        // entry that would prove it, and an entry written without the key
        // looks exactly the same.
        <p className="c42-audit-note">
          {plural(unsigned, 'entry', 'entries')} here {unsigned === 1 ? 'is' : 'are'} hashed but not signed:
          written before this deployment began signing, or without its key. One page of the
          trail cannot tell which.
        </p>
      )}
    </>
  );
}

function Toolbar(props: {
  ids: string;
  me: string | undefined;
  view: 'organisation' | 'own' | 'none' | undefined;
  chosen: Filters;
  people: ReadonlyMap<string, string | undefined>;
  filtered: boolean;
  onPick: (next: Partial<Filters>) => void;
  loadedAt: number;
  busy: 'refresh' | 'filter' | 'older' | undefined;
  onRefresh: () => void;
  count: number;
}) {
  const { chosen, me, people } = props;

  // Every kind the trail records, whether or not any are loaded: the server
  // filters the whole trail. A kind this build does not know, chosen before,
  // is kept so the control never shows a choice nobody made.
  const kinds = chosen.kind && !KINDS.some((k) => k.id === chosen.kind)
    ? [...KINDS, { id: chosen.kind, label: chosen.kind }]
    : KINDS;

  // Somebody reading their own entries has nobody else to choose.
  const choosesWho = props.view !== 'own';
  const who = [...people]
    .filter(([id]) => id !== me)
    .map(([id, as]) => ({ id, label: as ? `${as} (${id})` : id }))
    .sort((a, b) => a.label.localeCompare(b.label));
  if (chosen.actor && chosen.actor !== me && !people.has(chosen.actor)) who.push({ id: chosen.actor, label: chosen.actor });

  return (
    <div className="c42-audit-bar">
      <div className="c42-audit-pick">
        <label htmlFor={`${props.ids}-kind`}>Kind</label>
        <select id={`${props.ids}-kind`} className="c42-select" value={chosen.kind}
          onChange={(e) => props.onPick({ kind: e.target.value })}>
          <option value="">All kinds</option>
          {kinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
      </div>
      {choosesWho && (
        <div className="c42-audit-pick">
          <label htmlFor={`${props.ids}-who`}>Who</label>
          <select id={`${props.ids}-who`} className="c42-select" value={chosen.actor}
            onChange={(e) => props.onPick({ actor: e.target.value })}>
            <option value="">Everyone</option>
            {me && <option value={me}>You ({me})</option>}
            {who.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>
      )}
      {props.filtered && (
        <button className="c42-mini" onClick={() => props.onPick(ALL)}>Clear filters</button>
      )}
      <span className="c42-audit-sum" role="status">
        {props.busy === 'filter'
          ? 'Reading the entries that match…'
          : props.filtered
            ? `${plural(props.count, 'match', 'matches')} on this page. Filtered by the server, across the whole trail.`
            : `${plural(props.count, 'entry', 'entries')} on this page.`}
      </span>
      <span className="c42-audit-fresh">
        <span>Read at {TIME.format(props.loadedAt)}</span>
        <button className="c42-mini" disabled={props.busy !== undefined} onClick={props.onRefresh}>
          {props.busy === 'refresh' ? 'Refreshing…' : 'Refresh'}
        </button>
      </span>
    </div>
  );
}

function Entries(props: {
  top: RefObject<HTMLDivElement | null>;
  ids: string;
  rows: readonly AuditRecord[];
  me: string | undefined;
  broken: string | undefined;
  busy: boolean;
}) {
  // By calendar day in the viewer's zone, so each row needs only its time.
  const now = new Date();
  const days: Array<{ key: string; label: string; rows: AuditRecord[] }> = [];
  for (const r of props.rows) {
    const at = new Date(r.at);
    const key = dayKey(at);
    const last = days.at(-1);
    if (last?.key === key) last.rows.push(r);
    else days.push({ key, label: dayLabel(at, now), rows: [r] });
  }

  return (
    <div className="c42-audit-list" ref={props.top} aria-busy={props.busy || undefined}>
      {/* The column names, for the wide layout. Hidden from screen readers
          because every field already carries its own name. */}
      <div className="c42-audit-head" aria-hidden="true">
        <span>When</span><span>Who</span><span>What</span><span>Against</span>
        <span>Details</span><span>Via</span><span>Record</span>
      </div>
      {days.map((d) => (
        <section key={d.key} className="c42-audit-day" aria-label={d.label}>
          <h3>{d.label}</h3>
          <ol>
            {d.rows.map((r) => (
              <Entry key={r.hash} id={`${props.ids}-${r.hash.slice(0, 16)}`} r={r}
                me={props.me} broken={r.hash === props.broken} />
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

function Entry({ r, id, me, broken }: { r: AuditRecord; id: string; me: string | undefined; broken: boolean }) {
  const [open, setOpen] = useState(false);
  const words = wordsFor(r.action);
  const says = words.says?.(r);
  const as = recordedAs(r);
  const uses = new Set(words.uses ?? []);
  const facts = Object.entries(r.detail ?? {}).filter(([k]) => !uses.has(k));

  // What it was done to. A mode or a preset is the new state of a
  // conversation, so the conversation is the thing acted on.
  const onConversation = words.subjectIsNow || !r.subject;
  const target = onConversation ? r.sessionId : r.subject;
  const kindOf = onConversation
    ? (r.sessionId ? 'Conversation' : undefined)
    : typeof words.against === 'function' ? words.against(r) : words.against;
  const inConversation = !words.noConversation && r.sessionId && r.sessionId !== target ? r.sessionId : undefined;
  const now = words.subjectIsNow && r.subject ? `Now ${r.subject}` : undefined;
  const via = [
    r.hostProduct && `through ${r.hostProduct}`,
    r.deviceId && `machine ${r.deviceId}`,
    r.model && `model ${r.model}`,
    r.pluginId && `plugin ${r.pluginId}`,
  ].filter(Boolean) as string[];

  return (
    <li id={id} className={`c42-audit-row${broken ? ' broken' : ''}`}>
      <div className="c42-audit-f when">
        <span className="c42-audit-k">When</span>
        <time className="c42-audit-time" dateTime={r.at} title={r.at}>{TIME.format(new Date(r.at))}</time>
      </div>
      <div className="c42-audit-f what">
        <span className="c42-audit-k">What</span>
        <span className="c42-audit-v">
          <b>{headingOf(r)}</b>
          {(now ?? says) && <span className="c42-audit-sub">{now ?? says}</span>}
          {broken && <span className="c42-audit-flag">Does not match the hash it was written with</span>}
        </span>
      </div>
      <div className="c42-audit-f who">
        <span className="c42-audit-k">Who</span>
        <span className="c42-audit-v">
          {r.actor === me && <span className="c42-audit-you">You</span>}
          <span className="c42-audit-id">{r.actor}</span>
          {as && <span className="c42-audit-sub">{as}</span>}
        </span>
      </div>
      <div className="c42-audit-f against">
        <span className="c42-audit-k">Against</span>
        <span className="c42-audit-v">
          {target
            ? <span>{kindOf && <span className="c42-audit-sub">{kindOf} </span>}<span className="c42-audit-id">{target}</span></span>
            : <span className="c42-audit-none">Nothing named</span>}
          {inConversation && (
            <span className="c42-audit-sub">in conversation <span className="c42-audit-id">{inConversation}</span></span>
          )}
        </span>
      </div>
      {/* Only when there is something to say. On a phone each field is a line
          of its own, and a line reading "not recorded" on every card is noise
          that pushes the next entry off the screen. */}
      {via.length > 0 && (
        <div className="c42-audit-f via">
          <span className="c42-audit-k">Via</span>
          <span className="c42-audit-v">
            {via.map((v) => <span key={v} className="c42-audit-sub">{v}</span>)}
          </span>
        </div>
      )}
      {facts.length > 0 && (
        <div className="c42-audit-f facts">
          <span className="c42-audit-k">Details</span>
          <dl className="c42-audit-facts">
            {facts.map(([k, v]) => (
              <div key={k}><dt>{k}</dt><dd>{shown(v)}</dd></div>
            ))}
          </dl>
        </div>
      )}
      <div className="c42-audit-f rec">
        <span className="c42-audit-k">Record</span>
        <button className="c42-audit-recbtn" aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} the whole record, number ${r.seq}`}
          onClick={() => setOpen((was) => !was)}>#{r.seq}</button>
      </div>
      {open && <Whole r={r} />}
    </li>
  );
}

/**
 * The record as it is stored, field by field.
 *
 * For somebody matching this screen against a copy streamed elsewhere: the
 * sequence number and the two hashes are what line the two up, and the hash
 * form says which way the hash was computed.
 */
function Whole({ r }: { r: AuditRecord }) {
  const rows: Array<[string, string | undefined]> = [
    ['Sequence', String(r.seq)],
    ['Written', r.at],
    ['Action', r.action],
    ['Actor', r.actor],
    ['Subject', r.subject],
    ['Organisation', r.orgId],
    ['Conversation', r.sessionId],
    ['Machine', r.deviceId],
    ['Product', r.hostProduct],
    ['Model', r.model],
    ['Plugin', r.pluginId],
    ['Approved by', r.approvedBy],
    ...Object.entries(r.detail ?? {}).map(([k, v]) => [`detail.${k}`, shown(v)] as [string, string]),
    ['Hash form', r.hashForm ? String(r.hashForm) : '1, which did not cover the details'],
    ['Hash', r.hash],
    ['Previous', r.prev],
  ];
  return (
    <div className="c42-audit-panel">
      <dl>
        {rows.filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => (
          <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Where the list stops, and why there.
 *
 * With a cursor there is only one honest end: the start of the trail, or of
 * the entries that match. A server older than paging sends no cursor at all,
 * and a full page from one says nothing about what lies behind it, so that is
 * said instead of claiming the start.
 */
function End({ pages, filtered, full }: { pages: boolean; filtered: boolean; full: boolean }) {
  if (!pages && full) {
    return (
      <div className="c42-audit-end">
        <p>
          These are the newest entries this server sends. It is older than paging,
          so there is no way further back from here.
        </p>
      </div>
    );
  }
  return (
    <div className="c42-audit-end">
      <p>
        {filtered
          ? 'No older entries match. That is everything on the trail that does.'
          : 'That is the start of the trail.'}
      </p>
    </div>
  );
}
